import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Sparkles,
    Check,
    ChevronRight,
    FileText,
    UploadCloud,
    Bot,
    Calculator,
    FileSignature,
    X,
    Trophy,
} from "lucide-react";

const STEP_ICONS = {
    create: FileText,
    upload: UploadCloud,
    analyze: Bot,
    cost: Calculator,
    annex: FileSignature,
};

/**
 * Onboarding checklist for new customer users.
 *
 * Renders ONLY if:
 *   - User is NOT the SaaS super-admin (`user.is_admin === false`).
 *   - At least one step is still pending.
 *   - The user has not explicitly dismissed the tour (localStorage).
 *
 * The status of each step is *derived* from the customer's tenders — there is
 * no extra persistence required on the backend.
 */
export function OnboardingChecklist({ user, tenders }) {
    const navigate = useNavigate();
    const dismissKey = user?.user_id ? `onboarding_dismissed:${user.user_id}` : null;
    const [dismissed, setDismissed] = useState(
        () => (dismissKey ? localStorage.getItem(dismissKey) === "1" : false),
    );

    const steps = useMemo(() => {
        const hasTender = (tenders?.length || 0) > 0;
        const hasPliego = (tenders || []).some(
            (t) => t.pliego_storage_path || t.pliego_url,
        );
        const hasAnalysis = (tenders || []).some(
            (t) =>
                t.analysis_status === "analyzed" ||
                t.analysis_status === "ready" ||
                (t.analysis && t.analysis.summary),
        );
        const hasCost = (tenders || []).some(
            (t) =>
                (Array.isArray(t.cost_items) && t.cost_items.length > 0) ||
                (t.cost_breakdown && Object.keys(t.cost_breakdown || {}).length > 0),
        );
        const hasAnnex = (tenders || []).some(
            (t) => (t.annexes?.length || 0) > 0,
        );
        return [
            {
                id: "create",
                title: "Crea tu primera licitación",
                desc: "Indica el título y órgano de contratación o impórtala desde el buscador PLCSP.",
                cta: "Nueva licitación",
                to: "/app/nueva",
                done: hasTender,
            },
            {
                id: "upload",
                title: "Sube el pliego en PDF",
                desc: "PCAP, PPT o consolidado. La IA leerá el texto automáticamente.",
                cta: "Ir a licitaciones",
                to: "/app/licitaciones",
                done: hasPliego,
            },
            {
                id: "analyze",
                title: "Lanza el análisis IA",
                desc: "Claude Sonnet 4.5 te resume objeto, presupuesto, criterios y plazos.",
                cta: "Ver licitaciones",
                to: "/app/licitaciones",
                done: hasAnalysis,
            },
            {
                id: "cost",
                title: "Genera el escandallo",
                desc: "Tabla editable con personal, materiales, GG y BI. Cálculo en vivo.",
                cta: "Ver licitaciones",
                to: "/app/licitaciones",
                done: hasCost,
            },
            {
                id: "annex",
                title: "Cumplimenta un anexo con IA",
                desc: "DEUC, declaración responsable o solvencia — listo para AutoFirma.",
                cta: "Cumplimentar anexos",
                to: "/app/cumplimentar-anexos",
                done: hasAnnex,
            },
        ];
    }, [tenders]);

    const totalSteps = steps.length;
    const doneCount = steps.filter((s) => s.done).length;
    const allDone = doneCount === totalSteps;

    // Hide for SaaS super-admin (the owner already knows everything) or when
    // the user has dismissed and either completed everything or wants to skip.
    if (user?.is_admin) return null;
    if (dismissed && allDone) return null;
    if (dismissed && !allDone) return null;

    const dismiss = () => {
        if (dismissKey) localStorage.setItem(dismissKey, "1");
        setDismissed(true);
    };

    const nextStep = steps.find((s) => !s.done) || steps[0];
    const percent = Math.round((doneCount / totalSteps) * 100);

    return (
        <AnimatePresence>
            <motion.div
                key="onboarding"
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden rounded-3xl border border-brand-100 bg-gradient-to-br from-brand-50/70 via-white to-cyan-50/40 shadow-pop mb-6"
                data-testid="onboarding-checklist"
            >
                <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-brand-200/40 to-cyan-200/30 rounded-full blur-3xl pointer-events-none" />

                <button
                    type="button"
                    onClick={dismiss}
                    className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-white/70 hover:bg-white text-ink-400 hover:text-ink-700 flex items-center justify-center transition-colors backdrop-blur"
                    aria-label="Saltar el tour"
                    data-testid="onboarding-dismiss"
                >
                    <X size={15} />
                </button>

                <div className="relative p-6 md:p-8">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                        <div className="min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand-600 mb-2 flex items-center gap-2">
                                <Sparkles size={13} />
                                {allDone
                                    ? "Configuración completada"
                                    : "Empieza a sacarle partido"}
                            </div>
                            <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-ink-950 leading-tight">
                                {allDone ? (
                                    <>
                                        ¡Has completado el tour!{" "}
                                        <span className="text-brand-600">Buen trabajo.</span>
                                    </>
                                ) : (
                                    <>
                                        Tu primer pliego analizado en{" "}
                                        <span className="text-brand-600">5 pasos</span>.
                                    </>
                                )}
                            </h2>
                            <p className="text-ink-500 mt-2 text-sm md:text-base font-medium max-w-2xl">
                                {allDone
                                    ? "Ya conoces el flujo completo. Sigue creando licitaciones y la IA se encarga del resto."
                                    : "Te lo enseñamos paso a paso. Cada acción tarda menos de un minuto."}
                            </p>
                        </div>

                        {/* Big progress ring */}
                        <div className="flex items-center gap-4 shrink-0">
                            <ProgressRing percent={percent} />
                            <div>
                                <div className="font-display text-3xl md:text-4xl font-black text-ink-950 leading-none">
                                    {doneCount}
                                    <span className="text-ink-400 text-2xl"> /{totalSteps}</span>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-bold mt-1">
                                    Pasos hechos
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Steps grid */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        {steps.map((s, idx) => {
                            const Icon = STEP_ICONS[s.id] || FileText;
                            const isNext = !s.done && s.id === nextStep.id;
                            return (
                                <motion.button
                                    key={s.id}
                                    type="button"
                                    onClick={() => navigate(s.to)}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.05, duration: 0.4 }}
                                    whileHover={{ y: -3 }}
                                    className={`group relative text-left rounded-2xl p-4 border transition-all ${
                                        s.done
                                            ? "bg-white border-emerald-200 shadow-pop"
                                            : isNext
                                              ? "bg-brand-600 border-brand-700 text-white shadow-pop-lg ring-4 ring-brand-200/60"
                                              : "bg-white border-ink-100 hover:border-brand-300 shadow-pop"
                                    }`}
                                    data-testid={`onboarding-step-${s.id}`}
                                >
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div
                                            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                                                s.done
                                                    ? "bg-emerald-100 text-emerald-700"
                                                    : isNext
                                                      ? "bg-white/20 text-white"
                                                      : "bg-brand-50 text-brand-600"
                                            }`}
                                        >
                                            {s.done ? (
                                                <Check size={16} strokeWidth={3} />
                                            ) : (
                                                <Icon size={16} />
                                            )}
                                        </div>
                                        <span
                                            className={`font-mono text-[10px] font-bold uppercase tracking-wider ${
                                                s.done
                                                    ? "text-emerald-600"
                                                    : isNext
                                                      ? "text-white/70"
                                                      : "text-ink-400"
                                            }`}
                                        >
                                            {String(idx + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <div
                                        className={`font-display font-bold text-sm leading-snug mb-1 ${
                                            isNext ? "text-white" : "text-ink-950"
                                        }`}
                                    >
                                        {s.title}
                                    </div>
                                    <div
                                        className={`text-[11px] leading-snug ${
                                            isNext ? "text-white/80" : "text-ink-500"
                                        }`}
                                    >
                                        {s.desc}
                                    </div>
                                    {!s.done && isNext && (
                                        <div className="mt-3 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider">
                                            {s.cta}
                                            <ChevronRight
                                                size={12}
                                                className="transition-transform group-hover:translate-x-1"
                                            />
                                        </div>
                                    )}
                                </motion.button>
                            );
                        })}
                    </div>

                    {allDone && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="mt-6 flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-2xl p-4 shadow-pop"
                        >
                            <Trophy size={20} />
                            <div className="flex-1">
                                <div className="font-display font-bold text-base">
                                    Has dominado lo básico
                                </div>
                                <div className="text-sm text-white/90">
                                    Si nos quieres más, prueba el Buscador PLCSP o invita a un
                                    compañero de equipo.
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={dismiss}
                                className="bg-white text-emerald-700 rounded-full px-4 py-2 text-xs font-bold hover:bg-emerald-50 transition-colors"
                                data-testid="onboarding-finish"
                            >
                                Cerrar
                            </button>
                        </motion.div>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );
}

function ProgressRing({ percent }) {
    const r = 28;
    const c = 2 * Math.PI * r;
    const offset = c - (percent / 100) * c;
    return (
        <div className="relative w-16 h-16 shrink-0">
            <svg
                viewBox="0 0 64 64"
                className="w-16 h-16 -rotate-90"
                aria-hidden="true"
            >
                <circle
                    cx="32"
                    cy="32"
                    r={r}
                    stroke="#dbeafe"
                    strokeWidth="6"
                    fill="none"
                />
                <motion.circle
                    cx="32"
                    cy="32"
                    r={r}
                    stroke="url(#ring-grad)"
                    strokeWidth="6"
                    fill="none"
                    strokeLinecap="round"
                    initial={{ strokeDashoffset: c }}
                    animate={{ strokeDashoffset: offset }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    strokeDasharray={c}
                />
                <defs>
                    <linearGradient id="ring-grad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="#0055ff" />
                        <stop offset="1" stopColor="#22d3ee" />
                    </linearGradient>
                </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-display font-black text-sm text-ink-950">
                {percent}%
            </div>
        </div>
    );
}
